import { MESSAGE_TYPES, ENTRYPOINTS } from 'ai/constants/superchat';
import { getGENAISession } from 'ai/utils/sessionStorage';

class VisibilityContext {
    constructor({
        previousMessages,
        messages,
        config,
        isLoadingPrevious,
        contextualChipContextAware,
        newContextDetected,
        entryPointData,
        followUps,
        welcomeMatCompletedOnMount
    }) {
        this.previousMessagesCount = previousMessages.filter(msg => msg.response_type !== 'CAMPAIGN_CONFIG_RESPONSE')?.length;
        this.previousMessagesLength = previousMessages.length;
        this.messagesLength = messages.length;
        this.hasUserQuery = messages.some(msg => msg.message_type === MESSAGE_TYPES.USER_QUERY);
        this.config = config;
        this.isLoadingPrevious = isLoadingPrevious;
        this.contextualChipContextAware = contextualChipContextAware;
        this.newContextDetected = newContextDetected;
        this.entryPointData = entryPointData;
        this.followUpsLength = followUps.length;
        this.currentSession = getGENAISession();
        this.welcomeMatDataFromStorage = welcomeMatCompletedOnMount;
    }
}

class WelcomeMatEnabledStrategy {
    getVisibility(placement, ctx) {
        const welcomeMatCompleted = ctx.welcomeMatDataFromStorage;
        const isDefaultEntrypoint = ctx.entryPointData?.entrypoint === ENTRYPOINTS.DEFAULT;

        const isContextAwarePage =
            ctx.entryPointData?.entrypoint === ENTRYPOINTS.PDP ||
            ctx.entryPointData?.entrypoint === ENTRYPOINTS.PLP ||
            ctx.entryPointData?.entrypoint === ENTRYPOINTS.SRP;

        // Welcome mat should show on DEFAULT entrypoint when not completed and no contextual chip
        const isWelcomeMatVisible = !ctx.contextualChipContextAware && !welcomeMatCompleted && isDefaultEntrypoint;

        const rules = {
            welcomeMat: () => isWelcomeMatVisible,

            initialMessages: () => {
                const hasNoPreviousButHasCurrentMessages = ctx.previousMessagesCount === 0 && ctx.messagesLength > 0 && ctx.config;
                const isNonDefaultEntrypoint = ctx.entryPointData?.entrypoint !== ENTRYPOINTS.DEFAULT;

                return hasNoPreviousButHasCurrentMessages && (isNonDefaultEntrypoint || welcomeMatCompleted);
            },

            welcomeMessageAndDisclaimer: () => !ctx.isLoadingPrevious && ctx.previousMessagesCount > 0 && ctx.config,

            previousMessages: () => ctx.previousMessagesLength > 0,

            contextualChipAndSelectedPrompts: () => {
                // when there's context and welcome mat is NOT showing
                if (isWelcomeMatVisible) {
                    return false;
                }

                if (ctx.newContextDetected) {
                    return true;
                }

                if (ctx.currentSession?.sessionId === ctx.entryPointData?.sessionId) {
                    return false;
                }

                return ctx.contextualChipContextAware && ctx.messagesLength > 0;
            },

            initialPrompts: () => {
                const shouldShowInitialPrompts =
                    (welcomeMatCompleted || isContextAwarePage) && !ctx.contextualChipContextAware && ctx.followUpsLength === 0;

                return shouldShowInitialPrompts;
            },

            messages: () => true,

            contextualChip: () => {
                // Show follow-up chips when:
                // - Welcome mat is completed OR on context-aware pages
                // - No new context detected
                return (welcomeMatCompleted || isContextAwarePage) && !ctx.newContextDetected;
            }
        };

        const rule = rules[placement];

        return rule ? rule() : false;
    }
}

class WelcomeMatDisabledStrategy {
    getVisibility(placement, ctx) {
        const rules = {
            initialMessages: () => !ctx.isLoadingPrevious && ctx.previousMessagesLength === 0 && ctx.config,

            welcomeMessageAndDisclaimer: () => !ctx.isLoadingPrevious && ctx.previousMessagesLength > 0 && ctx.config,

            previousMessages: () => ctx.previousMessagesLength > 0,

            contextualChipAndSelectedPrompts: () => ctx.contextualChipContextAware && ctx.followUpsLength === 0,

            initialPrompts: () => !ctx.contextualChipContextAware && ctx.followUpsLength === 0,

            messages: () => true,

            contextualChip: () => ctx.messagesLength > 0 || (ctx.contextualChipContextAware && ctx.followUpsLength > 0)
        };

        const rule = rules[placement];

        return rule ? rule() : false;
    }
}

class DisplayVisibilityManager {
    constructor(strategy, context) {
        this.strategy = strategy;
        this.context = context;
    }

    isVisible(placement) {
        return this.strategy.getVisibility(placement, this.context);
    }
}

export const createDisplayVisibility = ({
    previousMessages,
    messages,
    config,
    isLoadingPrevious,
    contextualChipContextAware,
    newContextDetected,
    entryPointData,
    followUps,
    isWelcomeMatEnabled,
    welcomeMatCompletedOnMount
}) => {
    const context = new VisibilityContext({
        previousMessages,
        messages,
        config,
        isLoadingPrevious,
        contextualChipContextAware,
        newContextDetected,
        entryPointData,
        followUps,
        welcomeMatCompletedOnMount
    });

    const strategy = isWelcomeMatEnabled ? new WelcomeMatEnabledStrategy() : new WelcomeMatDisabledStrategy();

    const manager = new DisplayVisibilityManager(strategy, context);

    return placement => manager.isVisible(placement);
};
